import React, { useCallback, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import Modal from 'components/shared/Modal';
import Button from 'components/shared/Button';
import Spinner from 'components/shared/Spinner';

import savedArrangements from 'domains/saved_arrangements';

import {
  restoreArrangementAndAutoFit,
  updateUnavailableArrangement,
} from 'thunks/arrangement_thunks';

import type { UnavailableArrangementItem } from 'domains/saved_arrangements';

type UnavailableArrangementModalProps = {
  id: number;
  items: UnavailableArrangementItem[];
  onClose: () => void;
};

type DeleteSelection = 'arrangement' | 'discontinued' | 'unavailable' | 'both' | '';

function UnavailableArrangementModal(props: UnavailableArrangementModalProps) {
  const { id, items, onClose } = props;

  const dispatch = useAppDispatch();
  const arrangement = useAppSelector((state) => savedArrangements.selectors.selectById(state, id));

  if (!arrangement) return null;

  const unavailableItems = useMemo(
    () => items.filter((item) => item.reason === 'unavailable').map((item) => item.item),
    [items]
  );

  const discontinuedItems = useMemo(
    () => items.filter((item) => item.reason === 'discontinued'),
    [items]
  );

  const allItemsDiscontinued =
    arrangement && discontinuedItems.length === arrangement.canvases.length;
  const showDiscontinued = !allItemsDiscontinued && discontinuedItems.length > 0;
  const onlyDiscontinued = showDiscontinued && unavailableItems.length === 0;
  const onlyUnavailable = discontinuedItems.length === 0 && unavailableItems.length > 0;
  const showBoth = showDiscontinued && unavailableItems.length > 0;
  const showUnavailable = unavailableItems.length > 0;

  const pluralUnavailable = unavailableItems.length > 1;

  // Init this value to discontinued if one of the options _must_ be selected
  const [selection, setSelection] = useState<DeleteSelection>(() =>
    showBoth ? 'discontinued' : ''
  );
  const [loading, setLoading] = useState(false);

  const disableSelection = useMemo(() => {
    const count = unavailableItems.length + discontinuedItems.length;
    return (
      (selection === 'both' || selection === 'unavailable') &&
      count === arrangement?.canvases.length
    );
  }, [arrangement, discontinuedItems, selection, unavailableItems]);

  const handleSelection = useCallback(
    (value: DeleteSelection, e: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = e.target;
      // unavailable selection is a single checkbox, so unset if already set
      if (name === 'selection-check' && selection === value) {
        setSelection('');
      } else {
        setSelection(value);
      }
    },
    [selection]
  );

  const handleDeleteSelection = (selected: DeleteSelection) => {
    setLoading(true);
    let ids: string[] = [];

    if (selected === 'arrangement') {
      setSelection('arrangement'); // so we can open manage modal in the useeffect
      dispatch(savedArrangements.actions.destroy.pending(id));
    }
    if (selected === 'discontinued' || selected === 'both') {
      const discontinuedIds = discontinuedItems.map((item) => item.item.id);
      ids = ids.concat(discontinuedIds);
    }

    if (selected === 'unavailable' || selected === 'both') {
      const unavailableIds = unavailableItems.map((item) => item.id);
      ids = ids.concat(unavailableIds);
    }

    if (ids.length > 0) {
      dispatch(updateUnavailableArrangement(ids, arrangement));
    } else {
      dispatch(restoreArrangementAndAutoFit(arrangement));
    }

    onClose();
  };

  const primaryFooterGroup = (
    <React.Fragment>
      {onlyDiscontinued && (
        <Button type="outline" onClick={() => handleDeleteSelection('discontinued')}>
          Delete Discontinued Items
        </Button>
      )}
      {(onlyUnavailable || showBoth) && (
        <Button
          disabled={disableSelection}
          type="outline"
          onClick={() => handleDeleteSelection(selection)}
        >
          Continue
        </Button>
      )}
      <Button type="cancel" onClick={onClose}>
        Cancel
      </Button>
    </React.Fragment>
  );

  const secondaryFooterGroup = (
    <Button
      className="delete-arrangement"
      type="confirm"
      onClick={() => handleDeleteSelection('arrangement')}
    >
      Delete Entire Arrangement
    </Button>
  );

  const arrangementSummary = (
    <p>
      You are trying to restore the arrangement named <strong>{arrangement.name}</strong>, which has{' '}
      <strong>{arrangement.canvases.length}</strong> products.
    </p>
  );

  const allDiscontinuedMessage = (
    <p>
      All the items in this arrangement are permanently discontinued. Confirm if you want to delete
      this arrangement.
    </p>
  );

  const discontinuedMessage = (
    <p>
      There are items in this arrangement that have been permanently discontinued. You can choose to
      remove just the affected products, or delete this entire arrangement.
    </p>
  );

  const unavailableMessage = (
    <p>
      There {pluralUnavailable ? 'are' : 'is'} {showBoth ? 'also' : ''}
      {` ${unavailableItems.length} `}
      {pluralUnavailable ? ' selections' : ' selection'} in this arrangement that{' '}
      {pluralUnavailable ? 'are' : 'is'} temporarily unavailable. You can choose to remove{' '}
      {pluralUnavailable ? 'these items' : 'this item'}
      {showBoth ? ' as well' : ''}, or load the arrangement and make different selections.
    </p>
  );

  const deleteAllMessage = <p>This selection will delete all items in this arrangement.</p>;

  const unavailableItemsList = useMemo(() => {
    // Likely that an arrangement could have multiple items with the same option
    // selected, so just show the name once
    const itemsList = uniqBy(unavailableItems, 'name').map((item) => (
      <li key={`${item.id}-${item.name}`}>
        {item.parentName} - {item.name}
      </li>
    ));

    return (
      <section>
        <h2>Unavailable Selections</h2>
        <ul>{itemsList}</ul>
      </section>
    );
  }, [unavailableItems]);

  const selectionOptions = useMemo(() => {
    let inputs;
    if (showBoth) {
      inputs = (
        <React.Fragment>
          <label>
            <input
              type="radio"
              name="selection-radio"
              checked={selection === 'discontinued'}
              onChange={(e) => handleSelection('discontinued', e)}
            />
            Delete only discontinued
          </label>
          <label>
            <input
              type="radio"
              name="selection-radio"
              checked={selection === 'both'}
              onChange={(e) => handleSelection('both', e)}
            />
            Delete discontinued and unavailable
          </label>
        </React.Fragment>
      );
    } else {
      inputs = (
        <label>
          <input
            type="checkbox"
            name="selection-check"
            checked={selection === 'unavailable'}
            onChange={(e) => handleSelection('unavailable', e)}
          />
          Delete items with unavailable selections
        </label>
      );
    }

    return (
      <fieldset>
        <legend className="screen-reader-text">Select action</legend>
        {inputs}
      </fieldset>
    );
  }, [handleSelection, selection, showBoth]);

  return (
    <Modal
      title="Unavailable Items in Arrangement"
      labelledBy="unavailable-arrangement-modal"
      lastFocusedElementId={`special-content-menu-item-${id}`}
      modalClass={['unavailable-arrangements-modal']}
      onClose={onClose}
      primaryFooterGroup={primaryFooterGroup}
      secondaryFooterGroup={secondaryFooterGroup}
    >
      <React.Fragment>
        {loading && <Spinner size="50" color="#252525" />}
        {!loading && (
          <div>
            {!allItemsDiscontinued && arrangementSummary}
            {allItemsDiscontinued && allDiscontinuedMessage}
            {showDiscontinued && discontinuedMessage}
            {showUnavailable && unavailableMessage}
            {showUnavailable && unavailableItemsList}
            {showUnavailable && selectionOptions}
            {disableSelection && deleteAllMessage}
          </div>
        )}
      </React.Fragment>
    </Modal>
  );
}

export default UnavailableArrangementModal;
